import React, { useState, useEffect } from 'react';
import { AppUser, Property } from '../../types';
import { EnvelopeIcon, ExclamationTriangleIcon, XMarkIcon } from '../Icons';
import { CIEN_CUADRAS_BUTTON_BLUE } from '../../firebase/firebaseConfig';
import { BrochureEmailList } from './brochure/BrochureEmailList';
import { BrochureStatusFeedback } from './brochure/BrochureStatusFeedback';
import { sendBulkBrochureApi } from '../../services/bulkApiClient';
import { BulkSelectionPayload } from '../../server/bulk/bulkTypes';

interface BrochureModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedProperties?: Property[];
  selectionPayload?: BulkSelectionPayload;
  totalSelectedCount?: number;
  appUser: AppUser | null;
}

type ModalStatus = 'form' | 'loading' | 'success' | 'error';
const MAX_PROPERTIES = 30;

export const BrochureModal: React.FC<BrochureModalProps> = ({
  isOpen,
  onClose,
  selectedProperties = [],
  selectionPayload,
  totalSelectedCount,
  appUser,
}) => {
  const [status, setStatus] = useState<ModalStatus>('form');
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processedCount, setProcessedCount] = useState<number>(0);

  const effectiveCount = totalSelectedCount ?? (selectionPayload?.selectedIds?.length || selectedProperties.length);

  useEffect(() => {
    if (isOpen) {
      setStatus('form');
      setError(null);
      setEmails(appUser?.email ? [appUser.email] : []);
      setInputValue('');
    }
  }, [isOpen, appUser]);

  if (!isOpen) return null;

  const handleAddEmail = () => {
    const email = inputValue.trim();
    if (!email) return;
    if (!/^\S+@\S+\.\S+$/.test(email)) return setError(`'${email}' no es un correo válido.`);
    if (emails.includes(email)) return (setError(`El correo '${email}' ya fue añadido.`), setInputValue(''));
    setEmails([...emails, email]);
    setInputValue('');
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const finalEmails = [...emails];
    const newEmail = inputValue.trim();
    if (newEmail && /^\S+@\S+\.\S+$/.test(newEmail) && !emails.includes(newEmail)) finalEmails.push(newEmail);
    if (finalEmails.length === 0) return setError('Ingresa al menos un correo válido.');

    setStatus('loading');
    setError(null);
    try {
      const payload: BulkSelectionPayload = selectionPayload || {
        mode: 'EXPLICIT',
        selectedIds: selectedProperties.map((p) => p.id),
      };
      const res = await sendBulkBrochureApi({
        selection: payload,
        emails: finalEmails,
        baseUrl: window.location.origin,
        brokerName: appUser?.nombreCompleto || appUser?.displayName || '',
        brokerPhone: appUser?.celular || '',
      });
      setProcessedCount(res.processedCount);
      setEmails(finalEmails);
      setStatus('success');
    } catch (err: any) {
      console.error('Error enviando brochure:', err);
      setError(err?.message || 'Ocurrió un error al enviar la solicitud.');
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-80 backdrop-blur-sm z-[150] flex items-center justify-center p-4">
      <div className="relative bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-fade-in-up p-8">
        {status !== 'loading' && (
          <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-red-500">
            <XMarkIcon className="w-6 h-6" />
          </button>
        )}
        {status === 'loading' || status === 'success' ? (
          <BrochureStatusFeedback status={status} emails={emails} processedCount={processedCount} onClose={onClose} />
        ) : (
          <>
            <h3 className="text-xl font-black text-gray-900 text-center uppercase">Enviar Brochure Comercial</h3>
            <p className="text-sm text-gray-600 text-center mt-2">
              Se enviará un PDF con <span className="font-bold">{Math.min(effectiveCount, MAX_PROPERTIES)}</span> inmueble(s).
            </p>
            {effectiveCount > MAX_PROPERTIES && (
              <div className="mt-3 p-3 bg-yellow-50 border-l-4 border-yellow-400 text-xs text-yellow-800 font-bold flex items-center gap-2">
                <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500 flex-shrink-0" />
                <span>Límite excedido: Solo se enviarán los primeros {MAX_PROPERTIES} inmuebles.</span>
              </div>
            )}
            {error && <div className="mt-3 bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded text-xs">{error}</div>}
            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              <BrochureEmailList
                emails={emails}
                inputValue={inputValue}
                onInputChange={(e) => { setInputValue(e.target.value); setError(null); }}
                onKeyDown={(e) => { if (['Enter', ',', 'Tab'].includes(e.key)) { e.preventDefault(); handleAddEmail(); } }}
                onBlur={handleAddEmail}
                onRemoveEmail={(idx) => setEmails(emails.filter((_, i) => i !== idx))}
              />
              <div className="pt-2 flex flex-col sm:flex-row-reverse gap-3">
                <button
                  type="submit"
                  disabled={emails.length === 0 && !inputValue.trim()}
                  className="w-full sm:w-auto px-6 py-2.5 text-xs font-black uppercase text-white rounded-xl shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  style={{ backgroundColor: CIEN_CUADRAS_BUTTON_BLUE }}
                >
                  <EnvelopeIcon className="w-4 h-4" />
                  Enviar Brochure
                </button>
                <button type="button" onClick={onClose} className="px-4 py-2 text-xs font-bold text-gray-400 hover:text-gray-700">Cancelar</button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};
